import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const navigate = useNavigate();

  const handleForgotPassword = async () => {
    if (!email.trim() || !newPassword.trim() || !confirmNewPassword.trim()) {
      setError("Please fill in all fields.");
      return;
    }

    if (newPassword.trim().length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError("New password and confirm password do not match.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${baseUrl}/v2/auth/forgot-password`, {
        email: email.toLowerCase().trim(),
        newPassword: newPassword.trim(),
        confirmNewPassword: confirmNewPassword.trim(),
      });

      if (response.status === 200) {
        toast.success(
          "OTP sent to your email. Please verify to reset your password.",
          {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
          }
        );
        setShowOtpModal(true);
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Failed to send reset email. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpAndResetPassword = async () => {
    if (!otp) {
      setError("Please enter the OTP sent to your email.");
      return;
    }

    try {
      const response = await axios.post(`${baseUrl}/v2/auth/reset-password`, {
        email: email.toLowerCase().trim(),
        otp,
        newPassword: newPassword.trim(),
      });

      if (response.status === 200) {
        toast.success(
          "Password reset successfully! You can now login with your new password.",
          {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
          }
        );
        setShowOtpModal(false);
        navigate("/login");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "OTP verification failed.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row rounded-2xl shadow-lg overflow-hidden w-full mt-10">
      {/* Left Side - Robot Animation */}
      <div className="w-full md:w-1/2 bg-primary flex items-center justify-center p-8 relative order-first md:order-none">
        <img
          src="/images/tutor.webp"
          alt="Aanya"
          className="w-24 h-28 sm:w-60 sm:h-64 object-cover object-top rounded-2xl"
        />
        {/* <div className="absolute top-5 left-5 w-12 h-12 bg-purple-600 rounded-full shadow-md"></div>
        <div className="absolute bottom-5 left-5 w-16 h-16 bg-purple-600 rounded-full shadow-lg"></div> */}
      </div>

      {/* Right Side - Forgot Password Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <p className="text-gray-600">
              Enter your details to reset your password
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <input
                type="email"
                placeholder="Enter your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
              />
            </div>

            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="Enter New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-4 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                {showNewPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm New Password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="w-full p-4 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <button
              onClick={handleForgotPassword}
              disabled={loading}
              className="w-full bg-primary text-white py-4 rounded-xl hover:bg-primary-dark transition-colors font-medium disabled:opacity-50"
            >
              {loading ? "Sending OTP..." : "Send Reset OTP"}
            </button>

            <div className="text-center">
              <span className="text-gray-600">Remember your password? </span>
              <button
                onClick={() => navigate("/login")}
                className="text-primary font-medium hover:underline"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>

        {/* OTP Modal */}
        {showOtpModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-xl shadow-md w-96">
              <h2 className="text-xl font-semibold text-primary mb-4">
                Verify OTP
              </h2>
              <p className="text-sm text-gray-700 mb-3">
                We've sent a 6-digit OTP to your email. Please enter it below to
                reset your password.
              </p>
              <input
                type="text"
                maxLength="6"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter OTP"
                className="w-full p-3 border rounded-md focus:ring-primary focus:border-primary mb-4"
              />
              {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
              <div className="flex justify-end space-x-2">
                <button
                  className="bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition"
                  onClick={() => {
                    setShowOtpModal(false);
                    setOtp("");
                    setError("");
                  }}
                >
                  Cancel
                </button>
                <button
                  className="bg-primary text-white py-2 px-4 rounded-md hover:bg-primary-dark transition"
                  onClick={verifyOtpAndResetPassword}
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
